import React from 'react';
import { DatePicker, Divider, Form, Input, InputNumber, Radio } from 'antd';
import moment, { Moment } from 'moment';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { DropdownOption } from '@app/models/DropdownOption';

const DATE_FORMAT = 'YYYY-MM-DD';

// All additional-info values are grouped under this key in the form store
// so they never collide with main form fields (e.g. "Countries").
export const ADDITIONAL_INFO_KEY = 'pre_clearance_info';

export interface PreClearanceField {
  id: number;
  field_id: string;
  name: string;
  input_type: string;
  options?: Array<DropdownOption | string>;
  rules?: string[];
  status?: string;
}

type AdditionalInfoProps = {
  editable: boolean;
  categoryFields?: PreClearanceField[];
  productFields?: PreClearanceField[];
};

/* ---------- helpers ---------- */

const normalizeOptions = (options: Array<DropdownOption | string> = []): DropdownOption[] =>
  options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

const isRequired = (field: PreClearanceField) => field.rules?.includes('required') ?? false;

const isActive = (field: PreClearanceField) => !field.status || field.status === 'ACTIVE';

const toMoment = (value?: string | null) => (value ? moment(value) : null);
const fromMoment = (date?: Moment | null) => (date ? date.format(DATE_FORMAT) : null);

// Form stores dates as 'YYYY-MM-DD' strings; pickers need Moments.
const getDateItemProps = (inputType: string) => {
  switch (inputType) {
    case 'DATE':
      return {
        getValueProps: (value: string) => ({ value: toMoment(value) }),
        normalize: (date: Moment | null) => fromMoment(date),
      };
    case 'DATE_RANGE':
      return {
        getValueProps: (value?: [string, string]) => ({
          value: Array.isArray(value) ? [toMoment(value[0]), toMoment(value[1])] : null,
        }),
        normalize: (range?: [Moment | null, Moment | null] | null) =>
          range ? [fromMoment(range[0]), fromMoment(range[1])] : null,
      };
    default:
      return {};
  }
};

const formatReadOnly = (field: PreClearanceField, value: unknown): string => {
  if (value == null || value === '') return '';

  switch (field.input_type) {
    case 'BOOLEAN':
      return [true, 1, '1', 'true'].includes(value as any) ? 'Yes' : 'No';
    case 'SELECTION':
    case 'DROPDOWN': {
      const option = normalizeOptions(field.options).find((o) => String(o.value) === String(value));
      return option ? String(option.label) : String(value);
    }
    case 'DATE_RANGE':
      return Array.isArray(value) ? value.filter(Boolean).join(' – ') : String(value);
    default:
      return String(value);
  }
};

/* ---------- field renderers ---------- */

const renderEditableInput = (field: PreClearanceField): React.ReactNode => {
  const enter = `Enter ${field.name}`;
  const select = `Select ${field.name}`;

  switch (field.input_type) {
    case 'TEXTAREA':
      return <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder={enter} />;
    case 'NUMBER':
    case 'INTEGER':
    case 'DECIMAL':
      return (
        <InputNumber
          style={{ width: '100%' }}
          precision={field.input_type === 'INTEGER' ? 0 : undefined}
          placeholder={enter}
        />
      );
    case 'DATE':
      return <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} placeholder={select} />;
    case 'DATE_RANGE':
      return <DatePicker.RangePicker style={{ width: '100%' }} format={DATE_FORMAT} />;
    case 'SELECTION':
    case 'DROPDOWN':
      return (
        <BaseSelect
          allowClear
          showSearch
          optionFilterProp="label"
          options={normalizeOptions(field.options)}
          placeholder={select}
          style={{ width: '100%' }}
        />
      );
    case 'BOOLEAN':
      return (
        <Radio.Group
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
        />
      );
    default:
      return <Input placeholder={enter} />;
  }
};

interface ReadOnlyValueProps {
  field: PreClearanceField;
  value?: unknown; // injected by Form.Item
}

const ReadOnlyValue: React.FC<ReadOnlyValueProps> = ({ field, value }) => {
  const text = formatReadOnly(field, value);
  return field.input_type === 'TEXTAREA' ? (
    <Input.TextArea readOnly autoSize bordered={false} value={text} />
  ) : (
    <BaseInput readOnly value={text} />
  );
};

interface FieldGridProps {
  fields: PreClearanceField[];
  editable: boolean;
}

// Label on its own line; input slightly narrower than the column so the two columns breathe
const LABEL_COL = { span: 24 };
const WRAPPER_COL = { lg: { span: 16 }, md: { span: 18 }, span: 24 };

const FieldGrid: React.FC<FieldGridProps> = ({ fields, editable }) => (
  <BaseRow gutter={[32, 0]}>
    {fields.map((field) => {
      const rules = editable && isRequired(field) ? [{ required: true, message: `${field.name} is required` }] : [];

      return (
        <BaseCol key={field.field_id} xs={24} md={24} lg={12}>
          <Form.Item
            name={[ADDITIONAL_INFO_KEY, field.field_id]}
            label={field.name}
            rules={rules}
            labelCol={LABEL_COL}
            wrapperCol={WRAPPER_COL}
            colon={false}
            style={{ marginBottom: 10 }}
            {...(editable ? getDateItemProps(field.input_type) : {})}
          >
            {editable ? renderEditableInput(field) : <ReadOnlyValue field={field} />}
          </Form.Item>
        </BaseCol>
      );
    })}
  </BaseRow>
);

/* ---------- component ---------- */

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({ editable, categoryFields = [], productFields = [] }) => {
  const activeCategoryFields = categoryFields.filter(isActive);
  const activeProductFields = productFields.filter(isActive);

  if (!activeCategoryFields.length && !activeProductFields.length) {
    return <p>No additional fields available.</p>;
  }

  return (
    <div style={{ padding: '12px 0 0 16px' }}>
      {activeCategoryFields.length > 0 && <FieldGrid fields={activeCategoryFields} editable={editable} />}

      {activeProductFields.length > 0 && (
        <>
          {/* <Divider style={{ margin: '24px 0 16px' }}>Product Related Details</Divider> */}
          <FieldGrid fields={activeProductFields} editable={editable} />
        </>
      )}
    </div>
  );
};

export default AdditionalInfo;
